import { useCallback, useEffect, useState } from "react";
import { useDebounce } from "@/src/hooks/use-debounce";
import type { AddressGeocodeSuggestion, AddressGeocodingClient } from "@/src/lib/geocoding-client";
import type { USState } from "@/src/lib/us-states";
const MIN_QUERY_LENGTH = 3;
export function useAddressGeocodingSearch({
  searchTerm,
  client,
  stateCode,
  enabled = true
}: {
  searchTerm: string;
  client: AddressGeocodingClient;
  stateCode?: USState;
  enabled?: boolean;
}) {
  const debouncedSearchTerm = useDebounce(searchTerm.trim(), 200);
  const [suggestions, setSuggestions] = useState<AddressGeocodeSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled || debouncedSearchTerm.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setError(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    const searchAddresses = async () => {
      try {
        const results = await client.autocompletePlaces({
          input: debouncedSearchTerm,
          maxResults: 5,
          stateCode
        });
        if (!cancelled) {
          setSuggestions(results);
        }
      } catch (err) {
        if (!cancelled) {
          setSuggestions([]);
          setError(err instanceof Error ? err.message : "Failed to search addresses");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    void searchAddresses();
    return () => {
      cancelled = true;
    };
  }, [client, debouncedSearchTerm, enabled, stateCode]);
  const shouldShowSuggestions = useCallback((query: string) => enabled && query.trim().length >= MIN_QUERY_LENGTH, [enabled]);
  return {
    suggestions,
    isLoading,
    error,
    shouldShowSuggestions,
    minQueryLength: MIN_QUERY_LENGTH
  };
}